(ns practitest.frontend.forms3.entity-form.footer
  (:require [practitest.frontend.common-entities.metadata :refer [entities-metadata]]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.format-utils :as fu]
            [practitest.frontend.common.i18n :refer [format-translation
                                                     get-translation]]
            [practitest.frontend.common.mixpanel :as mixpanel]
            [practitest.frontend.components.forms :as forms]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.notifications :as notifications]
            [practitest.frontend.components.users :as users]
            [practitest.frontend.components2.buttons :as buttons]
            [practitest.frontend.components2.misc-components :as misc-components]
            [practitest.frontend.forms3.entity-form.forms :as f3.forms]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.subs :as subs]
            [practitest.frontend.common.keyboard-shortcuts :as keyboard-shortcuts]
            [re-frame.core :as re-frame]
            [reagent.core :as reagent]))

(defn display-form-errors [{:keys [get-field-props get-field-state]} form-errors form-ref toast-id]
  (let [body (apply conj
                    [:<> (get-translation "entity.validation.mandatory-fields"
                                          "The following mandatory fields are not filled in: ")]
                    (interpose
                      ", "
                      (for [[field] form-errors]
                        (let [field-props @(get-field-props field)
                              field-state @(get-field-state field)]
                          [misc-components/link-button
                           {:on-click (fn []
                                        (f3.forms/scroll-to-field form-ref field-props field-state))}
                           (:label field-props)]))))

        first-field-props @(get-field-props (ffirst form-errors))
        first-field-state @(get-field-state (ffirst form-errors))]
    (do
      (f3.forms/scroll-to-field form-ref first-field-props first-field-state)
      (notifications/notify! body
                             {:type       "error"
                              :auto-close false
                            ;; Single notification
                              :id         toast-id}))))

(defn auto-rerender-on-interval []
  (let [timeout-id (atom nil)
        do-update (fn do-update [this]
                    (let [{:keys [interval]} (reagent/props this)]
                      (reset! timeout-id (js/setTimeout (fn []
                                                          (reagent/force-update this)
                                                          (do-update this))
                                                        interval))))]
    (reagent/create-class
      {:component-did-mount    (fn [this]
                                 (do-update this))
       :component-will-unmount (fn [_]
                                 (when-some [id @timeout-id]
                                   (js/clearTimeout id)))
       :reagent-render         (fn [{:keys [render-fn]}]
                                 (render-fn))})))

(re-frame/reg-sub
  ::notification-state
  (fn [[_ form-id]]
    (re-frame/subscribe [::subs/form-state form-id]))
  (fn [form-state]
    (:status form-state)))

(defn- redirect-to-index-page [{:keys [entity-type]}]
  (let [index-route (get-in entities-metadata [entity-type :urls :index])]
    (dom/navigate! (broutes/get-url-view index-route))))

(defn validate-fields!
  "Validates form, trigger notification if validation errors are present
  and returns true if no validation errors found and false if there are
  validation errors."
  [{:keys [form-ref] :as form-props}]
  (let [toast-id "entity-form-notification"
        form-state (forms/force-form-validation! form-props)
        ;; Gather all field errors into single map
        form-errors (forms/collect-form-errors form-state)]

    (if (seq form-errors)
      ;; TODO: re-render when fields are filled in
      (do
        (display-form-errors form-props form-errors form-ref toast-id)
        false)
      (do
        (notifications/dismiss toast-id)
        true))))

(defn standard-save-form! [{:keys [on-save new?] :as form-props} options]
  (when (validate-fields! form-props)
    (on-save (assoc options
               :new? new?))))

(defn- last-update-text [updated-at updated-by]
  (let [users @(re-frame/subscribe [::subs/users])
        current-user @(re-frame/subscribe [::subs/current-user])
        time (fu/format-time-ago updated-at)]
    (if updated-by

      (format-translation
        (get-translation
          "entity.form.updated_at_by"
          "Updated %{time} by %{user}")
        :time time
        :user (if (= updated-by (:id current-user))
                (get-translation "entity.form.you" "you")
                (if-let [user (users/find-user users updated-by)]
                  (:name user)
                  (format-translation
                    (get-translation "entity.form.unknown_user" "Unknown User (%{id})")
                    :id updated-by))))

      (format-translation
        (get-translation
          "entity.form.updated_at"
          "Updated %{time}")
        :time time))))

(defn- status-indicator [status]
  (cond
    (= status "success") [:div.green-indicator]
    (= status "error")   [:span.error-indicator [icons/exclamation-circle-fill]]
    :else                nil))

(defn pull-changes-component [{:keys [form-id entity-type entity-id]}]
  [:div
   [:span (get-translation "entity.form.touched"
                           "You are not working on the latest version of this form.
                             Please pull the latest changes before saving yours.")]
   [buttons/button
    {:text (get-translation "generic.button.pull_changes" "Pull changes")
     :tertiary? true
     :on-click (fn []
                   (mixpanel/track "Collaborative Editing"  {"Action" "Pull Changes"
                                                             "Entity Type" (name entity-type)
                                                             "Entity ID" entity-id})
                   (let [project-id (:id @(re-frame/subscribe [::subs/current-project]))
                         fetch-options {:form-id form-id
                                        :entity-type entity-type
                                        :entity-id entity-id
                                        :project-id project-id
                                        :new? false}]
                        (re-frame/dispatch [:practitest.frontend.forms3.entity-form/fetch-entity fetch-options])
                        (re-frame/dispatch [:practitest.frontend.events/fetch-comments])
                        (re-frame/dispatch [:practitest.frontend.events/fetch-attachments])
                        (re-frame/dispatch [:practitest.frontend.events/fetch-history])
                        (when (= entity-type :testset)
                          (re-frame/dispatch [:practitest.frontend.testset2.events/fetch-instances])
                          (re-frame/dispatch [:practitest.frontend.testset2.events/fetch-graphs]))))}]])

(defn standard-cancel-button [{:keys [mixpanel-attrs dirty-fields on-cancel new?] :as form-props}]
  [buttons/button
   {:text (get-translation "generic.button.cancel" "Cancel")
    :disabled? (empty? @dirty-fields)
    :on-click (fn []
                (if new?
                  (do
                    (mixpanel/track "Cancel Create Entity" mixpanel-attrs)
                    (if-let [_has-history? (> (.-length js/window.history) 1)]
                      (js/history.back)
                      (redirect-to-index-page form-props)))
                  (do
                    (mixpanel/track "Cancel Save Entity" mixpanel-attrs)
                    (on-cancel))))}])

(defn standard-save-button [{:keys [mixpanel-attrs dirty-fields touched?] :as form-props} save-form!]
  (let [on-save-click (fn []
                        (mixpanel/track (str "Updated " (get mixpanel-attrs "Entity")) mixpanel-attrs)
                        (save-form! form-props {:close? false}))
        saving? @(re-frame/subscribe [::subs/progress? :practitest.frontend.common-entities.events/save-entity])]
    [:<>
     [keyboard-shortcuts/add-shortcut {:shortcut [:entity-form :save] :handler on-save-click}]
     [buttons/button
      {:test-id "form-save-btn"
       :primary? true
       :text (get-translation "generic.button.save" "Save")
       :disabled? (or (empty? @dirty-fields) touched? saving?)
       :on-click on-save-click}]]))

(defn footer [{:keys [edit? form-id buttons-override mixpanel-attrs new? dirty-fields touched?] :as form-props}]
  (let [{:keys [status updated-at updated-by message]} @(re-frame/subscribe [::notification-state form-id])
        saving? @(re-frame/subscribe [::subs/progress? :practitest.frontend.common-entities.events/save-entity])]
    [:div.form-footer
     {:class (dom/classes-pairs-v :error (= status "error"))}

     (when edit?
       [:<>
        (let [edit-locked? (get-in form-props [:base-entity :edit_locked])]
          [:div.updates-holder
           (if edit-locked?
             [:div.locked-message
              [icons/lock]
              [:span (get-translation "test.form.locked-edit"
                                      "This test is locked for changes per the Test Flow policy - only authorized users can edit it.")]]
             [:<>
              [status-indicator status]
              [:span
               {:class (dom/classes-pairs-v :error (= status "error")
                                            :success (= status "success"))}
               (if touched?
                 [pull-changes-component form-props]
                 (if (some? updated-at)
                   [auto-rerender-on-interval {:interval 1000
                                               :render-fn (fn []
                                                            [:div.message-text
                                                             (last-update-text updated-at updated-by)])}]
                   message))]])])

        [:div.form-actions
         (when (not-empty @dirty-fields)
           ;; TODO: customize text here for e.g. steps editor in test entity screen
           (let [fields-count (count @dirty-fields)]
             (format-translation
               (if (= fields-count 1)
                 (get-translation "entity.form.changed_property" "You've changed one property")
                 (get-translation "entity.form.changed_properties" "You've changed %{n} properties"))
               :n fields-count)))

         (if buttons-override
           [buttons-override form-props standard-save-form!]
           [:div.m-button-group
            [standard-cancel-button form-props]
            [standard-save-button form-props standard-save-form!]])]])

     (when new?
       (if buttons-override
         [buttons-override form-props standard-save-form!]
         [:div.m-button-group
          [standard-cancel-button form-props]
          [buttons/button
           {:text (get-translation "generic.button.create-and-close" "Create & Close")
            :icon (when saving? [icons/mux-spinner])
            :disabled? (or (empty? @dirty-fields) saving?)
            :on-click (fn []
                        (standard-save-form! form-props {:close? true}))}]
          [buttons/button
           {:primary? true
            :icon (when saving? [icons/mux-spinner])
            :disabled? (or (empty? @dirty-fields) saving?)
            :text (get-translation "generic.button.create" "Create")
            :on-click (fn []
                        (standard-save-form! form-props {:close? false}))}]]))]))
